import type { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';

import { routeTree } from './routeTree.gen';
import { RouteErrorComponent } from '@/app-shell/route-error';
import { queryClient } from '@/lib/query-client';
import { initialAuthState, type AuthState } from '@/lib/auth/auth-state';
import type { EnsuredProfile } from '@/lib/auth/use-auth';
import type { BreadcrumbKey } from '@/lib/i18n';

// `beforeLoad` guards read `context.auth`, and await `ensureProfile` to get a
// settled `frontendUserState` to route on.
export type RouterContext = {
  queryClient: QueryClient;
  auth: AuthState;
  ensureProfile: () => Promise<EnsuredProfile>;
};

// Default stub for the pre-mount context; the real one is injected in App.tsx.
const unreachableProfile: EnsuredProfile = {
  frontendUserState: null,
  hasUser: false,
  profile: null,
  availableTenants: [],
  currentTenant: null,
};

export const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: initialAuthState,
    ensureProfile: () => Promise.resolve(unreachableProfile),
  },
  scrollRestoration: true,
  // Every route match without its own `errorComponent` falls back here, which
  // is what makes the 403 handling global rather than per-page.
  defaultErrorComponent: RouteErrorComponent,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    breadCrumb?: BreadcrumbKey;
    /** Full-bleed backdrop for pre-app state screens (see `_auth` layout). */
    backgroundImage?: string;
  }
}
